import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Loader2 } from 'lucide-react';
import { API_URL } from '@/contexts/AuthContext';
import { formatDataHora } from '@/lib/format';
import { nomeDaForma } from '@/lib/pagamento';

const baixar = (pedidoId, comp, signal) =>
  axios.get(`${API_URL}/pedidos/${pedidoId}/comprovantes/${comp.file_id}`, { responseType: 'blob', signal });

// Abre o comprovante numa aba nova (aqui e na tela de Pedidos). Falha vira toast, nunca exceção.
export const abrirComprovante = async (pedidoId, comp) => {
  try {
    const r = await baixar(pedidoId, comp);
    const url = window.URL.createObjectURL(new Blob([r.data], { type: comp.content_type || 'application/octet-stream' }));
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 60000); // a aba nova já carregou
  } catch {
    toast.error('Erro ao abrir o comprovante');
  }
};

// Pré-visualização do comprovante dentro do documento (imagem ou PDF), sem abrir janela.
const PreviewComprovante = ({ pedidoId, comp }) => {
  const [url, setUrl] = useState(null);
  const [erro, setErro] = useState(false);
  const tipo = comp.content_type || '';

  useEffect(() => {
    let ativo = true;
    let obj = null;
    // trocar de pedido no meio do download cancela o arquivo anterior (pode ter até 10 MB)
    const download = new AbortController();
    setUrl(null);
    setErro(false);
    baixar(pedidoId, comp, download.signal)
      .then((r) => {
        if (!ativo) return;
        obj = URL.createObjectURL(new Blob([r.data], { type: comp.content_type || r.data.type || 'application/octet-stream' }));
        setUrl(obj);
      })
      .catch(() => ativo && setErro(true));
    return () => {
      ativo = false;
      download.abort();
      if (obj) URL.revokeObjectURL(obj);
    };
    // o arquivo só muda com o file_id; o resto do objeto pode vir novo a cada consulta da lista
  }, [pedidoId, comp.file_id]);

  const caixa = 'grid min-h-[220px] place-items-center rounded-[10px] border border-dashed border-border bg-sheet2 p-5 text-center text-[13px] text-gray-500';
  if (erro) return <div className={caixa}>Não foi possível carregar a pré-visualização.</div>;
  if (!url) return <div className={`${caixa} animate-pulse`}>Carregando o comprovante.</div>;
  if (tipo.startsWith('image/') && !/heic|heif/i.test(tipo)) {
    return <img src={url} alt={`Comprovante ${comp.filename}`} className="max-h-[440px] w-full rounded-[10px] bg-sheet2 object-contain" />;
  }
  if (tipo === 'application/pdf') {
    return <iframe src={url} title={`Comprovante ${comp.filename}`} className="h-[440px] w-full rounded-[10px] border border-border" />;
  }
  return (
    <div className={caixa}>
      <div>
        <p className="font-medium text-foreground">{comp.filename}</p>
        <p className="mt-1">Este tipo de arquivo não tem pré-visualização. Use "Abrir em tamanho real".</p>
      </div>
    </div>
  );
};

/**
 * Comprovantes do pedido: abas (com mais de um), pré-visualização e "Abrir em tamanho real".
 * No Financeiro fica ao lado de aprovar e rejeitar; na Gestão de Pedidos é só para ver.
 * Num pedido em duas partes, cada comprovante diz de qual parte é ("Pagamento 2").
 */
const ComprovantesPedido = ({ pedido }) => {
  const comps = pedido.comprovantes || [];
  const [ativoId, setAtivoId] = useState(comps[0]?.file_id || null);
  const [abrindo, setAbrindo] = useState(false);
  const comp = comps.find((c) => c.file_id === ativoId) || comps[0] || null;
  if (!comp) return null;

  const abrir = async () => {
    setAbrindo(true);
    await abrirComprovante(pedido.id, comp);
    setAbrindo(false);
  };

  return (
    <>
      {comps.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {comps.map((c) => (
            <button
              key={c.file_id}
              type="button"
              onClick={() => setAtivoId(c.file_id)}
              aria-pressed={comp.file_id === c.file_id}
              className={`max-w-[220px] truncate rounded-lg px-2.5 py-1 text-[12.5px] transition-colors ${
                comp.file_id === c.file_id ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' : 'bg-muted text-gray-600 hover:bg-accent dark:text-gray-300'
              }`}
            >
              {c.parte ? `Pagamento ${c.parte}: ` : ''}{c.filename}
            </button>
          ))}
        </div>
      )}
      <PreviewComprovante pedidoId={pedido.id} comp={comp} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12.5px] text-gray-500">
        <span className="min-w-0 truncate">
          {[
            comp.parte && `Pagamento ${comp.parte}`,
            comp.forma_pagamento && nomeDaForma(comp.forma_pagamento),
            comp.filename,
            comp.uploaded_by_name || 'Vendedor',
            comp.uploaded_at && formatDataHora(comp.uploaded_at),
            comp.tamanho && `${(comp.tamanho / 1024).toFixed(0)} KB`,
          ].filter(Boolean).join(' · ')}
        </span>
        <button
          type="button"
          onClick={abrir}
          disabled={abrindo}
          className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
          data-testid={`ver-comprovantes-${pedido.numero_pedido}`}
        >
          {abrindo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
          Abrir em tamanho real
        </button>
      </div>
    </>
  );
};

export default ComprovantesPedido;
